import React, { useCallback, useEffect, useState } from "react";
import {
  and,
  filter,
  fromPairs,
  includes,
  map,
  mergeRight,
  omit,
  pluck,
  toPairs,
  pickBy,
  propEq,
  find,
} from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, set } from "ramda";
import Grid from "@mui/material/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { notBlank } from "../util/ext";
import {
  useContentProviderEndpoint,
  useContentProviderEndpointCreate,
  useContentProviderEndpointUpdate,
} from "./contentProviderServices";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import { Box, List, ListItem, ListItemText, MenuItem } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import { Skeleton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";
import { useOrganizations } from "../user-admin/userServices";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = makeStyles(theme => ({
  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },

  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  list: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
  },

  checkboxLabelPlacement: {
    marginLeft: 0,
  },

  emptyList: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
    fontSize: "1.2rem",
    fontStyle: "italic",
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

const initialState = {
  fields: {
    name: "",
    filePath: "",
    metadataParser: "",
    defaultModules: [],
    overrides: {},
    defaults: {},
    forceIsbn: false,
    disableArchive: false,
    autoApprove: false,
    active: true,
  },
};

const ProviderEndpointDialog = ({
  open,
  contentProvider,
  contentProviderEndpointId,
  modules,
  onClose,
  onSave,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  const [organizations, setOrganizations] = useState([]);
  const organizationResponse = useOrganizations();
  useEffect(() => {
    if (organizationResponse.isSuccess) {
      setOrganizations(organizationResponse.data);
    }
  }, [organizationResponse.isSuccess, organizationResponse.data]);

  const findOrganization = organizationId => {
    const organization = find(
      propEq("organizationId", organizationId),
      organizations,
    );

    return organization.organizationName;
  };

  const onFieldChanged = field => value => {
    console.log(value);
    const val = value.target ? value.target.value : value;
    const fieldValue = val === "null" || val === "none" ? "" : val;

    const update = set(lensProp(field), fieldValue, fields);
    setFields(update);
  };

  const onCheckboxChanged = field => event => {
    const val = event.target.checked;
    const update = set(lensProp(field), val, fields);
    setFields(update);
  };

  const [selectedModules, setSelectedModules] = useState([]);
  const handleModulesChange = useCallback((_event, modules) => {
    setSelectedModules(modules);
  }, []);

  const [overrides, setOverrides] = useState({});
  const [override, setOverride] = useState({ field: "", value: "" });
  const handleOverrideChange = field => value => {
    const val = value.target ? value.target.value : value;
    const update = set(lensProp(field), val, override);

    setOverride(update);
  };

  const handleAddOverride = () => {
    const newObj = fromPairs([[override.field, override.value]]);
    const update = mergeRight(overrides, newObj);
    setOverrides(update);
    setOverride({ field: "", value: "" });
  };

  const handleRemoveOverride = field => {
    const update = omit([field], overrides);
    setOverrides(update);
  };

  const [ownerOverrides, setOwnerOverrides] = useState({});
  const [ownerOverride, setOwnerOverride] = useState({
    publisherName: "",
    organizationId: "",
  });
  const handleOwnerOverrideChange = publisher => value => {
    const val = value.target ? value.target.value : value;
    const update = set(lensProp(publisher), val, ownerOverride);

    setOwnerOverride(update);
  };

  const handleAddOwnerOverride = () => {
    const newObj = fromPairs([
      [ownerOverride.publisherName, ownerOverride.organizationId],
    ]);
    const update = mergeRight(ownerOverrides, newObj);
    setOwnerOverrides(update);
    setOwnerOverride({ publisherName: "", organizationId: "" });
  };

  const handleRemoveOwnerOverride = publisher => {
    const update = omit([publisher], ownerOverrides);
    setOwnerOverrides(update);
  };

  const [defaults, setDefaults] = useState({});
  const [fieldDefault, setFieldDefault] = useState({ field: "", value: "" });
  const handleDefaultChange = field => value => {
    const val = value.target ? value.target.value : value;
    const update = set(lensProp(field), val, fieldDefault);

    setFieldDefault(update);
  };

  const handleAddDefault = () => {
    const newObj = fromPairs([[fieldDefault.field, fieldDefault.value]]);
    const update = mergeRight(defaults, newObj);
    setDefaults(update);
    setFieldDefault({ field: "", value: "" });
  };

  const handleRemoveDefault = field => {
    const update = omit([field], defaults);
    setDefaults(update);
  };

  const cpeResult = useContentProviderEndpoint(
    contentProvider.id,
    contentProviderEndpointId,
  );
  useEffect(() => {
    if (cpeResult.isSuccess && cpeResult.data != null) {
      const contentProviderEndpoint = cpeResult.data;
      console.log(contentProviderEndpoint);
      const cpeFields = {
        name: contentProviderEndpoint.name,
        location: contentProviderEndpoint.location,
        metadataParser: contentProviderEndpoint.metadataParser,
        contentProviderId: contentProvider.id,
        defaultModules: notBlank(contentProviderEndpoint.defaultModules)
          ? contentProviderEndpoint.defaultModules
          : [],
        overrides: notBlank(contentProviderEndpoint.overrides)
          ? contentProviderEndpoint.overrides
          : {},
        ownerOverrides: notBlank(contentProviderEndpoint.ownerOverrides)
          ? contentProviderEndpoint.ownerOverrides
          : {},
        defaults: notBlank(contentProviderEndpoint.defaults)
          ? contentProviderEndpoint.defaults
          : {},
        disableArchive: notBlank(contentProviderEndpoint.disableArchive)
          ? contentProviderEndpoint.disableArchive || false
          : false,
        autoApprove: notBlank(contentProviderEndpoint.autoApprove)
          ? contentProviderEndpoint.autoApprove || false
          : false,
      };
      setFields(cpeFields);
      setOverrides(cpeFields.overrides);
      setOwnerOverrides(cpeFields.ownerOverrides);
      setDefaults(cpeFields.defaults);

      if (and(notBlank(modules), notBlank(cpeFields.defaultModules))) {
        setSelectedModules(
          filter(
            module => includes(module.id, cpeFields.defaultModules),
            modules,
          ),
        );
      }
    }
  }, [
    contentProviderEndpointId,
    contentProvider,
    modules,
    setFields,
    setOverrides,
    setDefaults,
    setSelectedModules,
    cpeResult.isSuccess,
    cpeResult.data,
  ]);

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const handleError = useCallback(
    (message, err = {}) => {
      if (err instanceof FetchError) {
        if (err.status === 401) {
          dispatch(logOut());
        } else {
          showAlert(message);
        }
      } else {
        if (notBlank(err)) {
          showAlert(err.message);
        } else {
          showAlert(message);
        }
      }
    },
    [dispatch],
  );

  const contentProviderEndpointCreate = useContentProviderEndpointCreate();
  const contentProviderEndpointUpdate = useContentProviderEndpointUpdate();
  const handleOnSave = useCallback(async () => {
    if (notBlank(fields.name)) {
      const mergedEndpoint = mergeRight(fields, {
        defaultModules: pluck("id", selectedModules),
        overrides,
        defaults,
        ownerOverrides,
        contentProviderId: contentProvider.id,
        active: true,
        id: contentProviderEndpointId,
      });

      try {
        if (notBlank(contentProviderEndpointId)) {
          await contentProviderEndpointUpdate.mutateAsync({
            resourceIds: {
              contentProviderId: contentProvider.id,
              contentProviderEndpointId,
            },
            data: pickBy(notBlank, mergedEndpoint),
          });
        } else {
          await contentProviderEndpointCreate.mutateAsync({
            resourceIds: { contentProviderId: contentProvider.id },
            data: pickBy(notBlank, mergedEndpoint),
          });
        }

        onSave(fields);
      } catch (err) {
        handleError("Unable to save Content Provider Endpoint.", err);
      }
    } else {
      handleError("Content Provider Endpoint Name is required.");
    }
  }, [
    handleError,
    onSave,
    contentProviderEndpointCreate,
    contentProviderEndpointUpdate,
    fields,
    selectedModules,
    overrides,
    ownerOverrides,
    defaults,
    contentProvider,
    contentProviderEndpointId,
  ]);

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth={"xl"} open={open} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">New Content Provider</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a data for the new Content Provider Endpoint.
        </DialogContentText>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <TextField
              id="name"
              label="Name"
              fullWidth
              value={fields.name || ""}
              onChange={onFieldChanged("name")}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              id="filePath"
              label="File Path"
              fullWidth
              value={fields.location || ""}
              onChange={onFieldChanged("location")}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth={true}>
              <InputLabel shrink id="parser-select-placeholder-label-label">
                Parser
              </InputLabel>
              <Select
                labelId="parser-select-placeholder-label-label"
                id="parser-select-placeholder-label"
                value={fields.metadataParser || ""}
                onChange={onFieldChanged("metadataParser")}
              >
                <MenuItem key="none" value="">
                  --
                </MenuItem>
                <MenuItem key="biblio-csv" value="BIBLIO_CSV">
                  BiblioLabs CSV
                </MenuItem>
                <MenuItem key="onix-21" value="ONIX_21">
                  ONIX 2.1
                </MenuItem>
                <MenuItem key="onix-30" value="ONIX_30">
                  ONIX 30
                </MenuItem>
                <MenuItem key="opds-v2" value="OPDS_V2">
                  OPDS 2
                </MenuItem>
                <MenuItem key="wiley-ml-xml" value="WILEY_ML_XML">
                  Wiley XML
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fields.autoApprove}
                  onChange={onCheckboxChanged("autoApprove")}
                  name="autoApprove"
                  color="primary"
                />
              }
              labelPlacement="start"
              label="Auto Approve"
              classes={{
                labelPlacementStart: classes.checkboxLabelPlacement,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={fields.disableArchive}
                  onChange={onCheckboxChanged("disableArchive")}
                  name="disableArchive"
                  color="primary"
                />
              }
              labelPlacement="start"
              label="Disable Archive"
              classes={{
                labelPlacementStart: classes.checkboxLabelPlacement,
              }}
            />
          </Grid>

          <Grid item sm={12}>
            {notBlank(modules) ? (
              <Box mt={4} className={classes.multiPicker}>
                <Autocomplete
                  multiple
                  id="modules"
                  options={modules}
                  getOptionLabel={option => option.name}
                  value={selectedModules || []}
                  filterSelectedOptions
                  onChange={handleModulesChange}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Default Modules"
                      placeholder="Select..."
                    />
                  )}
                />
              </Box>
            ) : (
              <Skeleton variant="text" />
            )}
          </Grid>

          {/* Overrides */}
          <Grid item xs={12}>
            <Typography variant="body1" className={classes.title}>
              Overrides
            </Typography>
            {notBlank(overrides) ? (
              <List classes={{ root: classes.list }}>
                {map(
                  override => (
                    <ListItem key={override[0]}>
                      <ListItemText
                        primary={override[0] + " -> " + override[1]}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => handleRemoveOverride(override[0])}
                          edge="end"
                          aria-label="delete"
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ),
                  toPairs(overrides),
                )}
              </List>
            ) : (
              <Box className={classes.emptyList}>Add an override...</Box>
            )}
          </Grid>
          <Grid item xs={5}>
            <TextField
              id="override-field"
              label="Field Name"
              type="text"
              value={override.field}
              onChange={handleOverrideChange("field")}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="override-value"
              label="Value"
              type="text"
              value={override.value}
              onChange={handleOverrideChange("value")}
              fullWidth
            />
          </Grid>
          <Grid item xs={1}>
            <div>
              <Button
                onClick={handleAddOverride}
                variant="text"
                color="primary"
              >
                Add
              </Button>
            </div>
          </Grid>

          {/* Owner Overrides */}
          <Grid item xs={12}>
            <Typography variant="body1" className={classes.title}>
              Owner Overrides
            </Typography>
            {notBlank(ownerOverrides) ? (
              <List classes={{ root: classes.list }}>
                {map(
                  override => (
                    <ListItem key={override[0]}>
                      <ListItemText
                        primary={
                          override[0] + " -> " + findOrganization(override[1])
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => handleRemoveOwnerOverride(override[0])}
                          edge="end"
                          aria-label="delete"
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ),
                  toPairs(ownerOverrides),
                )}
              </List>
            ) : (
              <Box className={classes.emptyList}>Add an owner override...</Box>
            )}
          </Grid>
          <Grid item xs={5}>
            <TextField
              id="owner-override-publisher-name"
              label="Publisher Name"
              type="text"
              value={ownerOverride.publisherName}
              onChange={handleOwnerOverrideChange("publisherName")}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <OrganizationAutoComplete
              value={ownerOverride.organizationId}
              filterFunction={org => org.active}
              onChange={handleOwnerOverrideChange(["organizationId"])}
            />
          </Grid>
          <Grid item xs={1}>
            <div>
              <Button
                onClick={handleAddOwnerOverride}
                variant="text"
                color="primary"
              >
                Add
              </Button>
            </div>
          </Grid>

          {/* Defaults */}
          <Grid item xs={12}>
            <Typography variant="body1" className={classes.title}>
              Defaults
            </Typography>
            {notBlank(defaults) ? (
              <List classes={{ root: classes.list }}>
                {map(
                  def => (
                    <ListItem key={def[0]}>
                      <ListItemText primary={def[0] + " -> " + def[1]} />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => handleRemoveDefault(def[0])}
                          edge="end"
                          aria-label="delete"
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ),
                  toPairs(defaults),
                )}
              </List>
            ) : (
              <Box className={classes.emptyList}>Add a default...</Box>
            )}
          </Grid>
          <Grid item xs={5}>
            <TextField
              id="default-field"
              label="Field Name"
              type="text"
              value={fieldDefault.field}
              onChange={handleDefaultChange("field")}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="default-value"
              label="Value"
              type="text"
              value={fieldDefault.value}
              onChange={handleDefaultChange("value")}
              fullWidth
            />
          </Grid>
          <Grid item xs={1}>
            <div>
              <Button onClick={handleAddDefault} variant="text" color="primary">
                Add
              </Button>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ProviderEndpointDialog;
